import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Box, FormHelperText, Typography } from '@material-ui/core'
import { getFileSignedUrl, deleteFile } from '@/redux/services/dynamicForm'

export type FileUploadConfig = {
  entityType: string
}

export type FileInputHandle = {
  upload: () => Promise<{ uploadedIds: string[] }>
  rollback: (ids: string[]) => Promise<void>
}

type Props = {
  label: string
  name: string
  required?: boolean
  helperText?: string
  accept?: string
  multiple?: boolean
  maxSizeMB?: number
  value: File | File[] | null
  onChange: (value: File | File[] | null) => void
  config?: FileUploadConfig
}

const FileInput = forwardRef<FileInputHandle, Props>(
  (
    {
      label,
      name,
      required,
      helperText,
      accept,
      multiple,
      maxSizeMB,
      value,
      onChange,
      config,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [error, setError] = useState<string | undefined>()

    useImperativeHandle(ref, () => ({
      upload: async () => {
        setError(undefined)
        if (!config) return { uploadedIds: [] }
        const files: File[] = Array.isArray(value)
          ? value
          : value
          ? [value]
          : []
        const uploadedIds: string[] = []
        for (const f of files) {
          const { signedUrl, remoteFileName } = await getFileSignedUrl({
            fileName: f.name,
            fileType: f.type || 'application/octet-stream',
            entityType: config.entityType,
          })
          const putRes = await fetch(signedUrl, {
            method: 'PUT',
            body: f,
            headers: { 'Content-Type': f.type || 'application/octet-stream' },
          })
          if (!putRes.ok) throw new Error('Failed to upload file')
          uploadedIds.push(remoteFileName)
        }
        return { uploadedIds }
      },
      rollback: async (ids: string[]) => {
        if (!config || !ids?.length) return
        await Promise.all(ids.map((id) => deleteFile(id)))
      },
    }))

    const onLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setError(undefined)
      const fl = e.target.files
      if (!fl) return onChange(null)
      const files = Array.from(fl)
      if (maxSizeMB) {
        const tooBig = files.some((f) => f.size > maxSizeMB * 1024 * 1024)
        if (tooBig) {
          setError(`Max file size is ${maxSizeMB} MB`)
          return
        }
      }
      onChange(multiple ? files : files[0])
    }

    return (
      <Box my={2}>
        <Typography variant="subtitle1" gutterBottom>
          {label}
          {required ? ' *' : ''}
        </Typography>
        <input
          ref={inputRef}
          type="file"
          name={name}
          accept={accept}
          multiple={multiple}
          onChange={onLocalChange}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
        {!error && helperText && <FormHelperText>{helperText}</FormHelperText>}
      </Box>
    )
  }
)

FileInput.displayName = 'FileInput'

export default FileInput
